import { AxiosInstance, AxiosRequestConfig } from 'axios'
// @ts-ignore
import AxiosService from '@deltax/api-client'

import { handleError } from '@/api/errorHandler'

const getPortalDomain = (): string => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_PORTAL_DOMAIN || 'roimatic.adbox.pro'
  }

  return window.location.hostname
}

const axiosService = new AxiosService(
  import.meta.env.VITE_API_BASE_URL,
  import.meta.env.VITE_APP_DELTAX_CLIENT_APP_ID,
  getPortalDomain(),
  handleError
)

export const axiosInstance = axiosService.axiosInstance
export const oAuth2AxiosInstance = axiosService.oAuth2AxiosInstance
export const apiMethods = axiosService.getApiMethods()

class ApiClient {
  protected readonly axiosInstance: AxiosInstance
  protected readonly oAuth2AxiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axiosInstance
    this.oAuth2AxiosInstance = oAuth2AxiosInstance
  }

  // Private method to decide which Axios instance to use
  private getAxiosInstance(useOAuth2: boolean): AxiosInstance {
    return useOAuth2 ? this.oAuth2AxiosInstance : this.axiosInstance
  }

  // Public method for GET requests
  public async get<T>(
    url: string,
    params?: AxiosRequestConfig['params'],
    config: AxiosRequestConfig = {},
    useOAuth2: boolean = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2)
    const response = await instance.get<T>(url, { params, ...config })
    return response.data
  }

  // Public method for POST requests
  public async post<T>(
    url: string,
    data: unknown = undefined,
    config: AxiosRequestConfig = {},
    useOAuth2: boolean = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2)
    const response = await instance.post<T>(url, data, config)
    return response.data
  }

  // Public method for PATCH requests
  public async patch<T>(
    url: string,
    data: unknown,
    config: AxiosRequestConfig = {},
    useOAuth2: boolean = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2)
    const response = await instance.patch<T>(url, data, config)
    return response.data
  }

  // Public method for DELETE requests
  public async delete<T>(
    url: string,
    config: AxiosRequestConfig = {},
    useOAuth2: boolean = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2)
    const response = await instance.delete<T>(url, config)
    return response.data
  }
}

export default ApiClient
